import { useEffect, useMemo, useState } from "react";
import { Drawer, message } from "antd";
import GooglePlacesAutocompleteInput from "../../common/GooglePlacesAutocompleteInput";
import { CareShell, Panel } from "../../components/care/CareLayout";
import { useWorkspace } from "../../contexts/WorkspaceContext";
import useAppConfig from "../../hooks/access-control/useAppConfig";
import { careService } from "../../services/careService";
import { addressFieldsFromPlaceDetails } from "../../utils/googlePlaces";

const pageItems = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.content)) return payload.content;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.items)) return payload.items;
  return [];
};

const firstText = (...values) =>
  values.map((value) => String(value || "").trim()).find(Boolean) || "";

const defaultPharmacyForm = () => ({
  name: "",
  phone: "",
  fax: "",
  email: "",
  website: "",
  addressSearch: "",
  addressLine1: "",
  addressLine2: "",
  city: "",
  state: "",
  postalCode: "",
  country: "",
  contactName: "",
  status: "ACTIVE",
  notes: "",
});

const pharmacyFormFromRecord = (pharmacy = {}) => ({
  ...defaultPharmacyForm(),
  name: pharmacy.name || "",
  phone: pharmacy.phone || "",
  fax: pharmacy.fax || "",
  email: pharmacy.email || "",
  website: pharmacy.website || "",
  addressSearch: pharmacyAddress(pharmacy) || pharmacy.addressLine1 || "",
  addressLine1: pharmacy.addressLine1 || "",
  addressLine2: pharmacy.addressLine2 || "",
  city: pharmacy.city || "",
  state: pharmacy.state || "",
  postalCode: pharmacy.postalCode || "",
  country: pharmacy.country || "",
  contactName: pharmacy.contactName || "",
  status: pharmacy.status || "ACTIVE",
  notes: pharmacy.notes || "",
});

const pharmacyAddress = (pharmacy = {}) =>
  [pharmacy.addressLine1, pharmacy.addressLine2, pharmacy.city, pharmacy.state, pharmacy.postalCode, pharmacy.country]
    .map((value) => String(value || "").trim())
    .filter(Boolean)
    .join(", ");

export default function PharmaciesPage() {
  const { selectedWorkspaceId, workspaceListReady } = useWorkspace();
  const { appConfig, loading: appConfigLoading } = useAppConfig();
  const googleMapsApiKey =
    appConfig?.googleMapsApiKey ||
    appConfig?.googleMapsApiMatrixKey ||
    process.env.REACT_APP_GOOGLE_MAPS_API_KEY ||
    process.env.VITE_GOOGLE_MAPS_API_KEY ||
    "";
  const [pharmacies, setPharmacies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState("");
  const [form, setForm] = useState(() => defaultPharmacyForm());
  const [search, setSearch] = useState("");
  const [error, setError] = useState("");

  const workspaceParams = useMemo(
    () => ({ wid: selectedWorkspaceId, workspaceId: selectedWorkspaceId }),
    [selectedWorkspaceId],
  );

  const loadPharmacies = () => {
    if (!workspaceListReady || !selectedWorkspaceId) {
      setPharmacies([]);
      return;
    }
    setLoading(true);
    setError("");
    careService.pharmacies.list(workspaceParams)
      .then((data) => setPharmacies(pageItems(data)))
      .catch((err) => {
        setPharmacies([]);
        setError(err?.response?.data?.message || err?.message || "Unable to load pharmacies.");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadPharmacies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWorkspaceId, workspaceListReady]);

  const filteredPharmacies = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return pharmacies;
    return pharmacies.filter((pharmacy) => [
      pharmacy.name,
      pharmacy.phone,
      pharmacy.email,
      pharmacyAddress(pharmacy),
    ].join(" ").toLowerCase().includes(term));
  }, [pharmacies, search]);

  const openNew = () => {
    setEditingId("");
    setForm(defaultPharmacyForm());
    setDrawerOpen(true);
  };

  const openEdit = (pharmacy) => {
    setEditingId(pharmacy?.id || "");
    setForm(pharmacyFormFromRecord(pharmacy));
    setDrawerOpen(true);
  };

  const updateForm = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const applyAddressFields = (fields = {}) => {
    setForm((current) => ({
      ...current,
      addressSearch: fields.search ?? current.addressSearch,
      addressLine1: fields.addressLine1 ?? current.addressLine1,
      city: fields.city ?? current.city,
      state: fields.state ?? current.state,
      postalCode: fields.postalCode ?? fields.zipCode ?? current.postalCode,
      country: fields.country ?? current.country,
    }));
  };

  const savePharmacy = async (event) => {
    event.preventDefault();
    if (!form.name.trim()) {
      message.error("Pharmacy name is required.");
      return;
    }
    setSaving(true);
    try {
      const { addressSearch, ...formPayload } = form;
      const payload = { ...formPayload, name: form.name.trim(), workspaceId: selectedWorkspaceId };
      const saved = editingId
        ? await careService.pharmacies.update(editingId, payload, workspaceParams)
        : await careService.pharmacies.create(payload, workspaceParams);
      setPharmacies((current) => [saved, ...current.filter((item) => item.id !== saved.id)]);
      setDrawerOpen(false);
      setEditingId("");
      message.success("Pharmacy saved.");
    } catch (err) {
      message.error(err?.response?.data?.message || err?.message || "Unable to save pharmacy.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <CareShell className="dashboard-content pharmacies-page">
      <div className="residents-page-head">
        <div className="page-title">
          <h1>Pharmacies</h1>
          <p>Manage pharmacies used to receive and coordinate resident medications.</p>
        </div>
        <button className="mini-btn pharmacy-primary-button" type="button" onClick={openNew}>
          <i className="ti ti-plus" /> Add Pharmacy
        </button>
      </div>

      <Panel title="Pharmacy Directory">
        {error ? <p className="form-error">{error}</p> : null}
        <label className="resident-search medication-resident-search">
          <i className="ti ti-search" />
          <input placeholder="Search pharmacies" value={search} onChange={(event) => setSearch(event.target.value)} />
        </label>
        <div className="medication-table-wrap">
          <table className="medication-table">
            <thead>
              <tr>
                <th>Pharmacy</th>
                <th>Contact</th>
                <th>Address</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan={5}>Loading pharmacies...</td></tr> : null}
              {!loading && !filteredPharmacies.length ? <tr><td colSpan={5}>No pharmacies found.</td></tr> : null}
              {!loading && filteredPharmacies.map((pharmacy) => (
                <tr key={pharmacy.id || pharmacy.name}>
                  <td>
                    <strong>{firstText(pharmacy.name, "Unnamed pharmacy")}</strong>
                    <small>{firstText(pharmacy.website, pharmacy.email, "")}</small>
                  </td>
                  <td>
                    {firstText(pharmacy.contactName, "Not added")}
                    <small>{firstText(pharmacy.phone, pharmacy.email, "")}</small>
                  </td>
                  <td>{firstText(pharmacyAddress(pharmacy), "Not added")}</td>
                  <td><em className="pill low">{firstText(pharmacy.status, "ACTIVE")}</em></td>
                  <td>
                    <div className="medication-row-actions">
                      <button className="resident-icon-button" type="button" aria-label={`Edit ${pharmacy.name}`} onClick={() => openEdit(pharmacy)}>
                        <i className="ti ti-edit" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

      <Drawer
        className="assessment-drawer medication-add-drawer"
        destroyOnClose
        onClose={() => setDrawerOpen(false)}
        open={drawerOpen}
        title={editingId ? "Edit Pharmacy" : "Add Pharmacy"}
        width="min(980px, 96vw)"
      >
        <form className="assessment-drawer-form" onSubmit={savePharmacy}>
          <section className="assessment-drawer-section">
            <h3>Pharmacy Information</h3>
            <div className="assessment-drawer-grid medication-add-grid">
              <label className="ncp-form-field"><span>Name</span><input value={form.name} onChange={(event) => updateForm("name", event.target.value)} required /></label>
              <label className="ncp-form-field"><span>Phone</span><input value={form.phone} onChange={(event) => updateForm("phone", event.target.value)} /></label>
              <label className="ncp-form-field"><span>Fax</span><input value={form.fax} onChange={(event) => updateForm("fax", event.target.value)} /></label>
              <label className="ncp-form-field"><span>Email</span><input type="email" value={form.email} onChange={(event) => updateForm("email", event.target.value)} /></label>
              <label className="ncp-form-field"><span>Website</span><input value={form.website} onChange={(event) => updateForm("website", event.target.value)} /></label>
              <label className="ncp-form-field"><span>Contact name</span><input value={form.contactName} onChange={(event) => updateForm("contactName", event.target.value)} /></label>
              <label className="ncp-form-field"><span>Status</span><select value={form.status} onChange={(event) => updateForm("status", event.target.value)}><option value="ACTIVE">Active</option><option value="INACTIVE">Inactive</option></select></label>
            </div>
          </section>
          <section className="assessment-drawer-section">
            <h3>Address</h3>
            <label className="ncp-form-field ncp-form-field-wide pharmacy-address-search">
              <span>Address search</span>
              {appConfigLoading && !googleMapsApiKey ? (
                <input type="text" disabled placeholder="Loading map address search..." />
              ) : (
                <GooglePlacesAutocompleteInput
                  googleMapsApiKey={googleMapsApiKey}
                  placeholder={googleMapsApiKey ? "Search pharmacy address" : "Enter pharmacy address"}
                  popupClassName="pharmacy-address-search-dropdown"
                  types={["address"]}
                  value={form.addressSearch}
                  onChange={(nextValue) => {
                    if (!nextValue) {
                      applyAddressFields({
                        search: "",
                        addressLine1: "",
                        city: "",
                        state: "",
                        postalCode: "",
                        country: "",
                      });
                      return;
                    }
                    updateForm("addressSearch", nextValue);
                    if (!googleMapsApiKey) updateForm("addressLine1", nextValue);
                  }}
                  onResolvePlace={async ({ details, prediction }) => {
                    const resolvedAddress = details?.formatted_address || prediction?.description || form.addressSearch || "";
                    const fields = details ? addressFieldsFromPlaceDetails(details) : {};
                    applyAddressFields({
                      ...fields,
                      search: resolvedAddress,
                      addressLine1: fields.addressLine1 || resolvedAddress,
                      postalCode: fields.postalCode || fields.zipCode || "",
                    });
                  }}
                />
              )}
            </label>
            <div className="assessment-drawer-grid medication-add-grid">
              <label className="ncp-form-field"><span>Address line 1</span><input value={form.addressLine1} onChange={(event) => updateForm("addressLine1", event.target.value)} /></label>
              <label className="ncp-form-field"><span>Address line 2</span><input value={form.addressLine2} onChange={(event) => updateForm("addressLine2", event.target.value)} /></label>
              <label className="ncp-form-field"><span>City</span><input value={form.city} onChange={(event) => updateForm("city", event.target.value)} /></label>
              <label className="ncp-form-field"><span>State</span><input value={form.state} onChange={(event) => updateForm("state", event.target.value)} /></label>
              <label className="ncp-form-field"><span>Postal code</span><input value={form.postalCode} onChange={(event) => updateForm("postalCode", event.target.value)} /></label>
              <label className="ncp-form-field"><span>Country</span><input value={form.country} onChange={(event) => updateForm("country", event.target.value)} /></label>
            </div>
            <label className="ncp-form-field"><span>Notes</span><textarea rows={4} value={form.notes} onChange={(event) => updateForm("notes", event.target.value)} /></label>
          </section>
          <div className="drawer-actions">
            <button className="mini-btn pharmacy-drawer-cancel" type="button" onClick={() => setDrawerOpen(false)}>Cancel</button>
            <button className="mini-btn pharmacy-primary-button" type="submit" disabled={saving}>{saving ? "Saving..." : "Save Pharmacy"}</button>
          </div>
        </form>
      </Drawer>
    </CareShell>
  );
}
